import React, { useCallback } from 'react';

import { Box, HStack, Text } from '@rbilabs/universal-components';
import { useStoreContext } from 'features/store';

import { IHistoricalOrder } from '@rbi-ctg/menu';
import ActionButton, { ActionButtonSizes } from 'components/action-button';
import { FavoriteOrderButton } from 'components/favorite-order-button';
import useOrderNumber from 'hooks/use-order-number';
import { IReorderEntry } from 'pages/menu/hooks/types';
import { useReorderEntries } from 'pages/menu/hooks/use-reorder-entries';
import { logAddRecentItemToCartEvent } from 'state/amplitude';
import { t } from 'state/intl/translations';
import { useReorder } from 'state/order/hooks/use-reorder';
import { useCreateOrderAriaLabel } from 'utils/accessibility/aria';
import { routes } from 'utils/routing';

import { RecentOrderCard } from './recent-order-card-list-item';

const Header = HStack.withConfig({
  paddingTop: '$1',
  paddingBottom: '$4',
  paddingX: '$3',
  // TODO: RN this breaks
  //backgroundColor: theme.recentOrderHeaderBackgroundColor,
  alignItems: 'center',
  justifyContent: 'space-between',
});

interface RecentOrderCardGroupProps {
  order: IHistoricalOrder;
}

const RecentOrderCardGroup: React.FC<React.PropsWithChildren<RecentOrderCardGroupProps>> = ({
  order,
}) => {
  const { store } = useStoreContext();
  const createAriaLabel = useCreateOrderAriaLabel();
  const { handleReorderClick } = useReorder(order, routes.menu);
  const reorderCartEntries = order?.cart.reorderCartEntries;
  const { reorderEntriesMap, checkingAvailability, hasSomeAvailableEntry } = useReorderEntries(
    reorderCartEntries
  );
  const orderNumber = useOrderNumber(order);
  const formattedDate = new Date(order.createdAt).toLocaleString(t('localeDateTime'), {
    month: 'long',
    day: 'numeric',
  });

  const handleReorder = useCallback(() => {
    if (order.cart?.reorderCartEntries) {
      for (const entry of order.cart.reorderCartEntries) {
        logAddRecentItemToCartEvent(entry?.name ?? '', store);
      }
      handleReorderClick();
    }
  }, [handleReorderClick, order.cart.reorderCartEntries, store]);

  if (!reorderCartEntries?.length) {
    return null;
  }

  return (
    <Box testID="recent-order-card-group" paddingBottom="$3">
      <Header>
        <HStack alignItems="center">
          <Text fontSize="sm" fontWeight="bold">
            {formattedDate}
          </Text>
          <FavoriteOrderButton order={order} />
          {orderNumber && (
            <Text fontSize="sm" marginLeft="$2">
              {t('order')} #{orderNumber}
            </Text>
          )}
        </HStack>
        <ActionButton
          testID="reorder-button"
          onPress={handleReorder}
          disabled={!hasSomeAvailableEntry}
          size={ActionButtonSizes.SMALL}
          aria-label={createAriaLabel({
            buttonType: 'reorder',
            order,
          })}
        >
          {t('reorder')}
        </ActionButton>
      </Header>
      {reorderCartEntries.map(cartEntry => {
        if (cartEntry.isDonation) {
          return null;
        }

        return (
          <RecentOrderCard
            key={cartEntry.lineId}
            cartEntry={cartEntry}
            mappedEntry={reorderEntriesMap[cartEntry.lineId] ?? ({} as IReorderEntry)}
            checkingAvailability={checkingAvailability}
          />
        );
      })}
    </Box>
  );
};

export default RecentOrderCardGroup;
